import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories, seoArticles } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const articleDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (articleDropdownRef.current && !articleDropdownRef.current.contains(e.target as Node)) {
        setArticleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleArticleClick = (slug: string) => {
    setMobileOpen(false);
    setArticleDropdownOpen(false);
    navigate(`/article/${slug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const navTextClass = scrolled
    ? 'text-foreground-800'
    : 'text-foreground-950';

  const navHoverClass = scrolled
    ? 'hover:text-foreground-950 hover:bg-background-100'
    : 'hover:text-foreground-950 hover:bg-white/40';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-background-50/60 backdrop-blur-sm'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 no-underline">
              <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors duration-300 ${navTextClass}`}>
                OOPBuy <span className="font-normal opacity-75">Spreadsheet</span>
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${navTextClass} ${navHoverClass}`}
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Home
            </Link>

            <div className="relative" ref={articleDropdownRef}>
              <button
                onClick={() => {
                  setArticleDropdownOpen(!articleDropdownOpen);
                  setDropdownOpen(false);
                }}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer ${navTextClass} ${navHoverClass}`}
              >
                <i className="ri-book-open-line mr-1"></i>
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${articleDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {articleDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 z-50">
                  <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">SEO Guides</p>
                  {seoArticles.map((article) => (
                    <button
                      key={article.slug}
                      onClick={() => handleArticleClick(article.slug)}
                      className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md bg-background-100 overflow-hidden">
                        <img src={article.image} alt="" className="w-full h-full object-cover" />
                      </span>
                      <span className="line-clamp-1">{article.category} Guide</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setArticleDropdownOpen(false);
                }}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer ${navTextClass} ${navHoverClass}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 z-50">
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md bg-background-100 overflow-hidden">
                          <img src={cat.image} alt="" className="w-full h-full object-cover" />
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="ml-3 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${navTextClass} hover:bg-background-100`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 shadow-lg">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100">
                <i className="ri-home-4-line text-primary-600"></i>
              </span>
              Home
            </Link>

            <div className="border-t border-background-200 my-2"></div>
            <p className="px-4 py-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Shopping Guides</p>
            {seoArticles.map((article) => (
              <button
                key={article.slug}
                onClick={() => handleArticleClick(article.slug)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer w-full text-left"
              >
                <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md bg-background-100 overflow-hidden">
                  <img src={article.image} alt="" className="w-full h-full object-cover" />
                </span>
                <span className="line-clamp-1">{article.category} Guide</span>
              </button>
            ))}

            <div className="border-t border-background-200 my-2"></div>
            <p className="px-4 py-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Categories</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md bg-background-100 overflow-hidden">
                    <img src={cat.image} alt="" className="w-full h-full object-cover" />
                  </span>
                  <span className="line-clamp-1">{cat.name}</span>
                </a>
              ))}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full text-center hover:bg-primary-600 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              OOPBuy Spreadsheet — Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}